import { useContext } from 'react';
import { Card, BlockStack, InlineStack, Text, Button } from '@shopify/polaris';
import { SessionContext } from '#client/contexts/SessionContext';
import { useLocationHelpers } from '#client/utils/helpers/cyclecounts';
import { formatDateToUTC, hasPermission } from '#client/utils/helpers';
import { STATUS_DISPLAY_MAPPING, TYPE_DISPLAY_MAPPING, STATUS } from '#client/pages/CycleCounts/constants/cycleCount';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import CycleCountActionButtons from '#client/pages/CycleCounts/Detail/components/CycleCountActionButtons';

const ApprovalSection = ({ cycleCount }) => (
    <BlockStack>
        {cycleCount.createdBy && (
            <InlineStack gap="200">
                <Text variant="bodyMd" fontWeight="semibold">
                    Created By:
                </Text>
                <Text variant="bodyMd">{cycleCount.createdBy}</Text>
            </InlineStack>
        )}
        {cycleCount.completedBy && (
            <InlineStack gap="200">
                <Text variant="bodyMd" fontWeight="semibold">
                    Completed By:
                </Text>
                <Text variant="bodyMd">{cycleCount.completedBy}</Text>
            </InlineStack>
        )}

        {cycleCount.managerApprovedBy && (
            <InlineStack gap="200">
                <Text variant="bodyMd" fontWeight="semibold">
                    Manager Approval:
                </Text>
                <Text variant="bodyMd">{cycleCount.managerApprovedBy}</Text>
            </InlineStack>
        )}

        {cycleCount.financeApprovedBy && (
            <InlineStack gap="200">
                <Text variant="bodyMd" fontWeight="semibold">
                    Finance Approval:
                </Text>
                <Text variant="bodyMd">{cycleCount.financeApprovedBy}</Text>
            </InlineStack>
        )}

        {cycleCount.rejectedBy && (
            <>
                <InlineStack gap="200">
                    <Text variant="bodyMd" fontWeight="semibold">
                        Rejected By:
                    </Text>
                    <Text variant="bodyMd">{cycleCount.rejectedBy}</Text>
                </InlineStack>
                <InlineStack gap="200">
                    <Text variant="bodyMd" fontWeight="semibold">
                        Rejection Reason:
                    </Text>
                    <Text variant="bodyMd" tone="critical">
                        {cycleCount.rejectionReason}
                    </Text>
                </InlineStack>
            </>
        )}
    </BlockStack>
);

// Main component
const CycleCountDetails = ({ cycleCount, onStartCount, onSubmitCount, onApprove, onReject, onUpdateStatus, onExport, loadingStates, isSubmitDisabled = false }) => {
    const { currentUser } = useContext(SessionContext);
    const { getLocationName, getLocationAddress, getLocationPhone } = useLocationHelpers(currentUser, cycleCount);

    if (!cycleCount) {
        return (
            <Card>
                <Text>Loading cycle count details...</Text>
            </Card>
        );
    }

    // Determine which view we're in based on which callbacks are provided
    const isPerformView = onStartCount || onSubmitCount;
    const isDetailView = onApprove || onReject || onUpdateStatus || onExport;

    // Perform view specific logic
    const canStartCount = hasPermission(currentUser, 'ccStart');
    const canUpdateCount = hasPermission(currentUser, 'ccUpdate');
    const disableStartCount = cycleCount.status === STATUS.IN_PROGRESS || cycleCount.status === STATUS.PREPARING;
    const disableSubmitCount = cycleCount.status === STATUS.PENDING || cycleCount.status === STATUS.PREPARING || cycleCount.status === STATUS.ERROR || isSubmitDisabled;

    return (
        <Card>
            <BlockStack gap="400">
                <Text as="h2" variant="headingLg">
                    Cycle Count Details
                </Text>

                <BlockStack gap="300">
                    <InlineStack gap="200">
                        <Text variant="bodyMd" fontWeight="semibold">
                            Cycle Count:
                        </Text>
                        <Text variant="bodyMd">{cycleCount.name}</Text>
                    </InlineStack>

                    <BlockStack gap="100">
                        <InlineStack gap="200">
                            <Text variant="bodyMd" tone="semibold">
                                Store:
                            </Text>
                            <Text variant="bodyMd">{getLocationName()}</Text>
                        </InlineStack>
                        {getLocationAddress().map((line, idx) => (
                            <Text key={idx} variant="bodySm" tone="subdued">
                                {line}
                            </Text>
                        ))}
                        <Text variant="bodySm" tone="subdued">
                            {getLocationPhone()}
                        </Text>
                    </BlockStack>

                    <BlockStack gap="100">
                        <Text variant="bodyMd" fontWeight="semibold">
                            Description:
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {cycleCount.description || 'N/A'}
                        </Text>
                    </BlockStack>

                    <InlineStack gap="200">
                        <Text variant="bodyMd" fontWeight="semibold">
                            Date Created:
                        </Text>
                        <Text variant="bodyMd">{formatDateToUTC(cycleCount.createdAt)}</Text>
                    </InlineStack>

                    <ApprovalSection cycleCount={cycleCount} />

                    <InlineStack gap="200">
                        <Text variant="bodyMd" tone="semibold">
                            Reason:
                        </Text>
                        <Text variant="bodyMd">{cycleCount.reason.reasonCode || 'N/A'}</Text>
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center">
                        <Text variant="bodyMd" fontWeight="semibold">
                            Status:
                        </Text>
                        <CustomBadge status={STATUS_DISPLAY_MAPPING[cycleCount.status]} />
                        <CustomBadge status={TYPE_DISPLAY_MAPPING[cycleCount.type?.toLowerCase()]} />
                    </InlineStack>
                </BlockStack>

                {/* Action Buttons */}
                <InlineStack align="end" gap="200">
                    {isPerformView && (
                        <>
                            {canStartCount && (
                                <Button variant="primary" onClick={onStartCount} disabled={disableStartCount}>
                                    Start Count
                                </Button>
                            )}
                            {canUpdateCount && (
                                <Button variant="primary" onClick={onSubmitCount} disabled={disableSubmitCount}>
                                    Submit Count
                                </Button>
                            )}
                        </>
                    )}
                    {isDetailView && (
                        <CycleCountActionButtons
                            status={cycleCount.status}
                            inventorySyncedAt={cycleCount.inventorySyncedAt}
                            inventoryAppliedSkus={cycleCount.inventoryAppliedSkus}
                            onApprove={onApprove}
                            onReject={onReject}
                            onUpdateStatus={onUpdateStatus}
                            onExport={onExport}
                            loadingStates={loadingStates}
                        />
                    )}
                </InlineStack>
            </BlockStack>
        </Card>
    );
};

export default CycleCountDetails;
